import axios, { AxiosRequestConfig } from "axios";
import { appendQueryToUrl, logger } from "@/utils";
import { QueryParamsForGetRequest } from "@/utils/types";
import { replaceNull } from "@/utils/replace-null";
import { getReasonPhrase, StatusCodes } from "http-status-codes";
import { helmValidation } from "@/guards/helm";
import * as z from "zod";
import { LoggerFields } from "@/utils/logger/types";
import { flatten, get, size } from "lodash";

interface ResponseOptions {
  keepHeaders: boolean;
}

async function fetchData<T, J>(
  config: AxiosRequestConfig,
  responseOptions: ResponseOptions | undefined,
  schema?: z.Schema<J>,
  isFlatResponse = true,
): Promise<T> {
  return axios
    .request({
      ...config,
      transformResponse: data => {
        let result;

        try {
          result = JSON.parse(data, replaceNull);
        } catch (error) {
          result = data;
        }

        if (
          result.Code !== StatusCodes.OK &&
          result.Code !== StatusCodes.NOT_FOUND
        ) {
          const msg =
            result.Message ??
            getReasonPhrase(StatusCodes.INTERNAL_SERVER_ERROR);

          throw {
            message: msg,
            status: result.Code,
          };

          // throw new Error(msg);
        }

        if (schema) {
          helmValidation(
            result.Data,
            schema,
            config.url ?? "cannot find url in request config",
            LoggerFields.Warning,
          );
        }

        if (!isFlatResponse) {
          return result;
        }

        const response = get(result, "Data", []);

        const transformedResponse =
          response && size(response) === 1
            ? flatten(Object.values(response))
            : response;

        return transformedResponse as T;
      },
    })
    .then(response => {
      if (
        response.status >= StatusCodes.OK &&
        response.status < StatusCodes.MULTIPLE_CHOICES
      ) {
        if (responseOptions?.keepHeaders) {
          return {
            headers: response.headers,
            data: response.data,
          };
        }

        return response.data;
      }

      return Promise.reject(response);
    })
    .catch(error => {
      if (!axios.isCancel(error)) {
        const msg =
          error?.message ?? getReasonPhrase(StatusCodes.INTERNAL_SERVER_ERROR);

        logger.critical(msg);
        throw error;
      }

      return error;
    });
}

async function send<T, D = Record<string, unknown>, J = unknown>(
  url: string,
  options: AxiosRequestConfig,
  data?: D,
  responseOptions?: ResponseOptions | undefined,
  schema?: z.Schema<J>,
  isFlatResponse?: boolean,
) {
  let _data;

  const additionalHeader: Record<string, string | number | boolean> = {
    "Content-Type": "application/json",
    "x-mobile-request": true,
  };

  const headers = {
    ...additionalHeader,
    ...options.headers,
  };

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (headers?.["Content-Type"] === "application/json") {
    _data = JSON.stringify(data);
  } else {
    _data = data;
  }

  return fetchData<T, J>(
    {
      url,
      ...options,
      data: _data,
      headers,
    },
    responseOptions,
    schema,
    isFlatResponse,
  );
}

export async function postRequest<T, D = Record<string, unknown>, J = unknown>(
  url: string,
  data?: D,
  options?: AxiosRequestConfig,
  schema?: z.Schema<J>,
  isFlatResponse?: boolean,
): Promise<T> {
  return send<T, D, J>(
    url,
    { ...options, method: "POST" },
    data,
    undefined,
    schema,
    isFlatResponse,
  );
}

export async function updateRequest<
  T,
  D = Record<string, unknown>,
  J = unknown,
>(
  url: string,
  data?: D,
  options?: AxiosRequestConfig,
  schema?: z.Schema<J>,
  isFlatResponse?: boolean,
): Promise<T> {
  return send<T, D, J>(
    url,
    { ...options, method: "PUT" },
    data,
    undefined,
    schema,
    isFlatResponse,
  );
}

export async function deleteRequest<T, J = unknown>(
  url: string,
  queryParams?: QueryParamsForGetRequest,
  options?: AxiosRequestConfig,
  responseOptions?: ResponseOptions | undefined,
  schema?: z.Schema<J>,
  isFlatResponse?: boolean,
): Promise<T> {
  let requestUrl = url;

  if (queryParams !== undefined) {
    requestUrl = appendQueryToUrl(url, queryParams);
  }

  return send<T, unknown, J>(
    requestUrl,
    { ...options, method: "DELETE" },
    undefined,
    responseOptions,
    schema,
    isFlatResponse,
  );
}

export async function getRequest<T, J = unknown>(
  url: string,
  queryParams?: QueryParamsForGetRequest,
  options?: AxiosRequestConfig,
  responseOptions?: ResponseOptions | undefined,
  schema?: z.Schema<J>,
  isFlatResponse?: boolean,
): Promise<T> {
  let requestUrl = url;

  if (queryParams !== undefined) {
    requestUrl = appendQueryToUrl(url, queryParams);
  }

  return send<T, unknown, J>(
    requestUrl,
    { ...options, method: "GET" },
    undefined,
    responseOptions,
    schema,
    isFlatResponse,
  );
}
